/*
Copyright (c) 2012-2024 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import React, { useState, useEffect, useMemo } from "react"
import { jsx } from "@emotion/core"
import { useTranslationsMap } from "common/utils/i18n"
import { Col, Input, Row, Select, Switch } from "@duxton-web/ui"
import { Form } from "grab-ui"
import { useSelector } from "react-redux"
import { AppState } from "store/store.type"
import {
  eventValueToCleanNumericInput,
  getValidationProps,
  newDeferredValidator,
  stringValidations,
} from "utils/form_helper"
import { itemWeightUnits } from "utils/item_helper"
import classNames from "classnames"
import { drawerContentStyle, dashStyle, disabledSoldByWeight } from "../styles"
import { EditableFieldsMap } from "./constant"

interface IProps {
  form: WrappedFormUtils
  weightKey?: string
  isAllowEdit: boolean
  editableFields?: Pick<EditableFieldsMap, "soldByWeight" | "weight">
}

function weightValueGetValueFromEvent(
  e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
) {
  return eventValueToCleanNumericInput(e, 2)
}

export function ItemSizeFieldsDrawerSection({
  form,
  weightKey = "weight",
  isAllowEdit,
  editableFields = {
    soldByWeight: true,
    weight: true,
  },
}: IProps) {
  const item = useSelector((state: AppState) => state.menu.drawer.item)

  const [weightCount, setWeightCount] = useState(item?.weight?.count)

  const [isSoldByWeightEnabled, setIsSoldByWeightEnabled] = useState(
    item?.soldByWeight,
  )

  useEffect(() => {
    form.validateFields([weightKey], {
      force: true,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weightCount, isSoldByWeightEnabled])

  const { getFieldDecorator } = form
  const weightValidationProps = getValidationProps(
    form,
    [`${weightKey}[unit]`, `${weightKey}[value]`, `${weightKey}[count]`],
    true,
  )

  const textMap = useTranslationsMap({
    errorRequired: "menu.errors.requiredField",
    enterNumberBiggerZero: "menu.errors.number.enterNumberBiggerZero",
    itemSize: "catalogue.item.itemSize",
    numOfPiecesPerItemLabel: "catalogue.item.labels.numOfPiecesPerItem",
    numOfPiecesPerItemPlaceholder:
      "catalogue.item.placeholders.numOfPiecesPerItem",
    numOfPiecesPerItemHint: "catalogue.item.hints.numOfPiecesPerItem",
    sizeDifferentPerUnit: "catalogue.item.labels.soldByWeight",
    sizePerUnitLabel: "catalogue.item.labels.sizePerUnit",
    sizePerUnitValue: "catalogue.item.placeholders.sizePerUnit.value",
    sizePerUnitUnit: "catalogue.item.placeholders.sizePerUnit.unit",
  })

  const deferredValidator = useMemo(
    () =>
      newDeferredValidator(form, [
        {
          message: textMap.enterNumberBiggerZero,
          pattern: stringValidations.moreThanZero,
          ignoreEmptyValue: true,
        },
      ]),
    [form, textMap.enterNumberBiggerZero],
  )

  const onChangeWeightCount = (e: any) => {
    const countVal = e?.target?.value
    setWeightCount(Number(countVal))
  }

  const handleChangeSoldByWeight = (value: boolean) => {
    setIsSoldByWeightEnabled(value)
  }

  const isWeightValueRequired = () =>
    Number(weightCount) > 0 || isSoldByWeightEnabled

  return (
    <React.Fragment>
      <div className="section-separator">
        <span>{textMap.itemSize}</span>
      </div>
      <div css={drawerContentStyle}>
        <Row>
          <Col span={24}>
            <Form.Item
              label={<span>{textMap.numOfPiecesPerItemLabel}</span>}
              {...weightValidationProps[`${weightKey}[count]`]}
              {...(!weightValidationProps[`${weightKey}[count]`].help && {
                help: textMap.numOfPiecesPerItemHint,
              })}
            >
              {getFieldDecorator(`${weightKey}[count]`, {
                initialValue: item?.weight?.count,
                getValueFromEvent: eventValueToCleanNumericInput,
                rules: [
                  {
                    message: textMap.enterNumberBiggerZero,
                    pattern: stringValidations.moreThanZero,
                  },
                ],
              })(
                <Input
                  data-testid="input-weight-count"
                  disabled={!isAllowEdit || !editableFields.weight}
                  placeholder={textMap.numOfPiecesPerItemPlaceholder}
                  onChange={onChangeWeightCount}
                />,
              )}
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <div className="grab-ui ant-form-item-label">
              <label
                className={classNames({
                  "grab-ui": true,
                  "ant-form-item-required": isWeightValueRequired(),
                })}
              >
                {textMap.sizePerUnitLabel}
              </label>
            </div>
          </Col>
          <Col span={11}>
            <Form.Item {...weightValidationProps[`${weightKey}[value]`]}>
              {getFieldDecorator(`${weightKey}[value]`, {
                initialValue: item?.weight?.value,
                validateFirst: true,
                getValueFromEvent: weightValueGetValueFromEvent,
                rules: [
                  {
                    required: isWeightValueRequired(),
                    message: textMap.errorRequired,
                  },
                  {
                    validator:
                      deferredValidator.onChangeRuleErrorClearerValidator,
                  },
                ],
              })(
                <Input
                  data-testid="input-weight-value"
                  placeholder={textMap.sizePerUnitValue}
                  disabled={!isAllowEdit || !editableFields.weight}
                  onBlur={e =>
                    deferredValidator.validateOnSave(
                      e.target.value,
                      `${weightKey}[value]`,
                    )
                  }
                />,
              )}
            </Form.Item>
          </Col>
          <Col span={2} css={dashStyle}>
            -
          </Col>
          <Col span={11}>
            <Form.Item {...weightValidationProps[`${weightKey}[unit]`]}>
              {getFieldDecorator(`${weightKey}[unit]`, {
                initialValue: item?.weight?.unit ?? "ml",
              })(
                <Select
                  data-testid="select-weight-unit"
                  style={{
                    width: "100%",
                  }}
                  disabled={!isAllowEdit || !editableFields.weight}
                  placeholder={textMap.sizePerUnitUnit}
                >
                  {itemWeightUnits.map(unit => (
                    <Select.Option key={unit} value={unit}>
                      {unit}
                    </Select.Option>
                  ))}
                </Select>,
              )}
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <Form.Item>
              {getFieldDecorator("soldByWeight", {
                initialValue: item?.soldByWeight,
                valuePropName: "checked",
              })(
                <Switch
                  disabled={!isAllowEdit || !editableFields.soldByWeight}
                  data-testid="input-soldbyweight"
                  onChange={handleChangeSoldByWeight}
                />,
              )}{" "}
              <span css={!isSoldByWeightEnabled && disabledSoldByWeight}>
                {textMap.sizeDifferentPerUnit}
              </span>
            </Form.Item>
          </Col>
        </Row>
      </div>
    </React.Fragment>
  )
}
